import { useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  buildPartnerEarnIdempotencyKey,
  isDuplicatePartnerEarnCredit,
} from '~/components/Loyalty/PartnerEarn/partnerEarnCredit';

import { creditPartnerEarn } from '~/services/LuxLoyaltyService';

import type {
  AdminPartnerCreditBody,
  PartnerEarnBulkResult,
  PartnerEarnCsvRow,
  PartnerEarnRowResult,
} from '~/types/loyaltyContract';

const BATCH_SIZE = 5;

interface SubmitPartnerEarnParams {
  partnerId: string;
  rows: Array<PartnerEarnCsvRow>;
}

interface UseSubmitPartnerEarnOptions {
  onSuccess?: (data: PartnerEarnBulkResult) => void;
  onError?: (error: Error) => void;
}

function toCreditBody(row: PartnerEarnCsvRow): Omit<AdminPartnerCreditBody, 'idempotencyKey'> {
  return {
    accountNumber: row.accountNumber,
    activityTitle: row.activityTitle,
    ...(row.basePoints !== undefined ? { basePoints: row.basePoints } : {}),
    ...(row.statusCredits !== undefined ? { statusCredits: row.statusCredits } : {}),
    source: {
      reason: row.reason,
      ...(row.sourceKey ? { key: row.sourceKey } : {}),
    },
  };
}

function rowResultFields(row: PartnerEarnCsvRow): Omit<PartnerEarnRowResult, 'success' | 'transactionId' | 'error'> {
  return {
    accountNumber: row.accountNumber,
    activityTitle: row.activityTitle,
    reason: row.reason,
    basePoints: row.basePoints,
    statusCredits: row.statusCredits,
    sourceKey: row.sourceKey,
  };
}

async function creditRow(partnerId: string, row: PartnerEarnCsvRow): Promise<PartnerEarnRowResult> {
  const credit = toCreditBody(row);
  const idempotencyKey = buildPartnerEarnIdempotencyKey(partnerId, credit);
  try {
    const response = await creditPartnerEarn(partnerId, { ...credit, idempotencyKey });
    return {
      ...rowResultFields(row),
      idempotencyKey,
      success: true,
      transactionId: response.transactionId,
    };
  } catch (err) {
    // A 409 means the service already holds this exact credit. The row was accepted, but nothing
    // new was queued, so it is reported apart from the rows this run actually created.
    if (isDuplicatePartnerEarnCredit(err)) {
      return {
        ...rowResultFields(row),
        idempotencyKey,
        success: true,
        alreadyCredited: true,
      };
    }
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { ...rowResultFields(row), idempotencyKey, success: false, error: message };
  }
}

export function useSubmitPartnerEarn(options: UseSubmitPartnerEarnOptions = {}) {
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const processedRef = useRef(0);

  const mutation = useMutation({
    mutationFn: async ({ partnerId, rows }: SubmitPartnerEarnParams): Promise<PartnerEarnBulkResult> => {
      const results: Array<PartnerEarnRowResult> = [];
      processedRef.current = 0;
      setProgress({ processed: 0, total: rows.length });

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(batch.map((row) => creditRow(partnerId, row)));
        results.push(...batchResults);
        processedRef.current += batch.length;
        setProgress({ processed: processedRef.current, total: rows.length });
      }

      const alreadyCreditedCount = results.filter((result) => result.alreadyCredited).length;
      const successCount = results.filter((result) => result.success && !result.alreadyCredited).length;
      const failedCount = results.filter((result) => !result.success).length;

      return {
        totalProcessed: results.length,
        successCount,
        alreadyCreditedCount,
        failedCount,
        results,
      };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });

  return { ...mutation, progress };
}
